<template>
  <div>
    <DropZone
      v-model="dropZone.visible"
      :closable="dropZone.closable"
      :title="dropZone.title"
      :placeholder="dropZone.placeholder"
      :file-types="['image/*']"
      multiple
      @onEmbedImages="onEmbedImages"
      @closeDropZone="dropZone.visible = false"
    />

    <editor-menu-bar
      v-if="editable"
      v-slot="{ commands, isActive }"
      :editor="editor"
      class="editor__menubar"
    >
      <div class="menubar">
        <Tooltip
          content="Header 1"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ level: 1 }) }"
            @click.prevent="commands.heading({ level: 1 })"
          >
            H1
          </button>
        </Tooltip>
        <Tooltip
          content="Header 2"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ level: 2 }) }"
            @click.prevent="commands.heading({ level: 2 })"
          >
            H2
          </button>
        </Tooltip>
        <Tooltip
          content="Header 3"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ level: 3 }) }"
            @click.prevent="commands.heading({ level: 3 })"
          >
            H3
          </button>
        </Tooltip>
        <Tooltip
          content="Bullet List"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click.prevent="commands.bullet_list"
          >
            <i class="fas fa-list" />
          </button>
        </Tooltip>
        <Tooltip
          content="Ordered List"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click.prevent="commands.ordered_list"
          >
            <i class="fas fa-list-ol" />
          </button>
        </Tooltip>
        <Tooltip
          content="Quote"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.blockquote() }"
            @click.prevent="commands.blockquote"
          >
            <i class="fas fa-quote-right" />
          </button>
        </Tooltip>
        <Tooltip
          content="Insert Image"
          placement="top"
        >
          <button
            class="menubar__button"
            @click.prevent="showDropZone"
          >
            <i class="far fa-image" />
          </button>
        </Tooltip>
        <Tooltip
          content="Bold"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.bold() }"
            @click.prevent="commands.bold"
          >
            <i class="fas fa-bold" />
          </button>
        </Tooltip>
        <Tooltip
          content="Underline"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.underline() }"
            @click.prevent="commands.underline"
          >
            <i class="fas fa-underline" />
          </button>
        </Tooltip>
        <Tooltip
          content="Italic"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.italic() }"
            @click.prevent="commands.italic"
          >
            <i class="fas fa-italic" />
          </button>
        </Tooltip>
        <Tooltip
          content="Strike"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.strike() }"
            @click.prevent="commands.strike"
          >
            <i class="fas fa-strikethrough" />
          </button>
        </Tooltip>
        <Tooltip
          content="Inline Code"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.code() }"
            @click.prevent="commands.code"
          >
            <i class="fas fa-code" />
          </button>
        </Tooltip>
        <Tooltip
          content="Code Block"
          placement="top"
        >
          <button
            class="menubar__button"
            :class="{ 'is-active': isActive.code_block() }"
            @click.prevent="commands.code_block"
          >
            <i class="fas fa-terminal" />
          </button>
        </Tooltip>
        <Tooltip
          content="Undo"
          placement="top"
        >
          <button
            class="menubar__button"
            @click.prevent="commands.undo"
          >
            <i class="fas fa-undo" />
          </button>
        </Tooltip>
        <Tooltip
          content="Redo"
          placement="top"
        >
          <button
            class="menubar__button"
            @click.prevent="commands.redo"
          >
            <i class="fas fa-redo" />
          </button>
        </Tooltip>
        <Tooltip
          content="Create Table"
          placement="top"
        >
          <button
            class="menubar__button"
            @click.prevent="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })"
          >
            <i class="fas fa-table" />
          </button>
        </Tooltip>
        <span v-if="isActive.table()">
          <Tooltip
            content="Delete Table"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.deleteTable"
            >
              <icon name="delete_table" />
            </button>
          </Tooltip>
          <Tooltip
            content="Add Column Before"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.addColumnBefore"
            >
              <icon name="add_col_before" />
            </button>
          </Tooltip>
          <Tooltip
            content="Add Column After"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.addColumnAfter"
            >
              <icon name="add_col_after" />
            </button>
          </Tooltip>
          <Tooltip
            content="Delete Column"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.deleteColumn"
            >
              <icon name="delete_col" />
            </button>
          </Tooltip>
          <Tooltip
            content="Add Row Before"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.addRowBefore"
            >
              <icon name="add_row_before" />
            </button>
          </Tooltip>
          <Tooltip
            content="Add Row After"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.addRowAfter"
            >
              <icon name="add_row_after" />
            </button>
          </Tooltip>
          <Tooltip
            content="Delete Row"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.deleteRow"
            >
              <icon name="delete_row" />
            </button>
          </Tooltip>
          <Tooltip
            content="Merge Cell"
            placement="top"
          >
            <button
              class="menubar__button"
              @click.prevent="commands.toggleCellMerge"
            >
              <icon name="combine_cells" />
            </button>
          </Tooltip>
        </span>
      </div>
    </editor-menu-bar>
    <editor-menu-bubble
      v-if="editable"
      v-slot="{ commands, isActive, getMarkAttrs, menu }"
      :editor="editor"
      class="menububble"
      @hide="hideLinkMenu"
    >
      <div
        class="menububble"
        :class="{ 'is-active': menu.isActive }"
        :style="{ left: `${menu.left}px`, bottom: `${menu.bottom}px` }"
      >
        <Form
          v-if="linkMenuIsActive"
          class="menububble__form"
          @submit.native.prevent="setLinkUrl(commands.link, linkUrl)"
        >
          <Input
            ref="linkInput"
            v-model="linkUrl"
            type="text"
            placeholder="https://"
            class="menububble__input"
            @keydown.esc="hideLinkMenu"
          />
          <button
            type="button"
            class="menububble__button"
            @click="setLinkUrl(commands.link, null)"
          >
            <icon name="remove" />
          </button>
        </form>
        <template v-else>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.link() }"
            style="display: flex; flex-direction: row; align-items: center;"
            @click.prevent="showLinkMenu(getMarkAttrs('link'))"
          >
            <span>{{ isActive.link() ? 'Update Link' : 'Add Link' }}</span>
            <i
              class="fas fa-link"
              style="padding-left: 5px; font-size: 10px;"
            />
          </button>
        </template>
      </div>
    </editor-menu-bubble>
    <div
      v-show="showSuggestions"
      ref="suggestions"
      class="suggestion-list"
    >
      <template v-if="hasResults">
        <div
          v-for="(user, index) in filteredUsers"
          :key="user.id"
          class="suggestion-list-item"
          :class="{ 'is-selected': navigatedUserIndex === index }"
          @click="selectUser(user)"
        >
          {{ user.name }}
        </div>
      </template>
      <div
        v-else
        class="suggestion-list-item is-empty"
      >
        No users found
      </div>
    </div>
    <editor-content
      :editor="editor"
      :style="editorContentStyle"
      class="editor__content"
    />
    <PreviewContainer
      :editable="editable"
      :files="attachments"
      @onDeleteFile="deleteFile"
    />
  </div>
</template>

<script>
import tippy from 'tippy.js'
import _ from 'lodash'
import Icon from './EditorIcon'
import { fuzzySearch } from '@/helpers/fuzzy-search'
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from 'tiptap'
import {
  CodeBlockHighlight,
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Image,
  Link,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  Strike,
  Underline,
  History,
  TrailingNode,
  Mention
} from 'tiptap-extensions'

import { mapState } from 'vuex'
import DropZone from '../dropzone/DropZone'
import PreviewContainer from '../dropzone/Preview-Container'
import javascript from 'highlight.js/lib/languages/javascript'
import css from 'highlight.js/lib/languages/css'
import sql from 'highlight.js/lib/languages/sql'

import 'tippy.js/themes/light.css'
import '@/styles/text-editor/syntaxhighlight.scss'
import '@/styles/text-editor/main.scss'

export default {
  components: {
    EditorMenuBar,
    EditorMenuBubble,
    EditorContent,
    Icon,
    DropZone,
    PreviewContainer
  },

  model: {
    prop: 'value',
    event: 'input'
  },

  props: {
    value: {
      type: Object,
      default: () => {}
    },
    editable: {
      type: Boolean,
      default: false
    },
    defaultContentJson: {
      type: String,
      default: ''
    },
    defaultContentHtml: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      editor: null,
      isDropZoneActive: false,
      dropZone: {
        visible: false,
        closable: true,
        title: 'Upload Images',
        placeholder: 'Drop images here or click to select'
      },
      images: [],
      attachments: [],
      linkUrl: null,
      linkMenuIsActive: false,
      query: null,
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {},
      observer: null
    }
  },

  computed: {
    ...mapState('user', {
      users: 'allUsers'
    }),

    showSuggestions () {
      return this.query || this.hasResults
    },

    hasResults () {
      return this.filteredUsers.length
    },

    editorContentStyle () {
      return {
        '--min-height': this.editable ? '200px' : '18px',
        '--max-height': this.editable ? `${window.innerHeight * 0.5}px` : 'auto',
        '--border': this.editable ? '1px solid var(--color-gray-border)' : 'none',
        '--background-color': this.editable ? 'var(--color-white)' : 'transparent'
      }
    },

    defaultContent () {
      const parsedJsonContent = this.defaultContentJson ? JSON.parse(this.defaultContentJson) : {}
      return !_.isEmpty(parsedJsonContent) ? parsedJsonContent : this.defaultContentHtml
    }
  },

  watch: {
    defaultContentHtml (newVal) {
      if (newVal) {
        this.editor.setContent(this.defaultContentHtml)
        this.editor.focus()
      }
    },

    value () {
      this.attachments = (this.value || {}).ATTACHMENTS || []
    },

    linkUrl () {
      if (this.linkUrl && !this.linkUrl.startsWith('https://')) {
        this.linkUrl = `https://${this.linkUrl}`
      }
    },

    editable () {
      if (this.editor.options) {
        this.$set(this.editor.options, 'editable', this.editable)
        this.editor.focus('end')
      }
    }
  },

  mounted () {
    this.initEditor()
  },

  beforeDestroy () {
    this.destroyEditor()
  },

  methods: {
    initEditor () {
      this.editor = new Editor({
        content: this.defaultContent,
        autoFocus: true,
        editable: this.editable,
        disablePasteRules: ['italic'],
        extensions: [
          new Blockquote(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new TodoItem(),
          new TodoList(),
          new Bold(),
          new Code(),
          new CodeBlock(),
          new Italic(),
          new Image(),
          new Link(),
          new Strike(),
          new Underline(),
          new History(),
          new Table({
            resizable: true
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow(),
          new CodeBlockHighlight({
            languages: {
              javascript,
              css,
              sql
            }
          }),
          new TrailingNode({
            node: 'paragraph',
            notAfter: ['paragraph']
          }),
          new Mention({
            items: () => this.users, // [{id, name, email}]

            // is called when a suggestion starts
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.renderPopup(virtualNode)
              // we save the command for inserting a selected mention to call it inside of our custom popup via keyboard navigation and on click
              this.insertMention = command
            },

            // is called when a suggestion has changed
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.navigatedUserIndex = 0
              this.renderPopup(virtualNode)
            },

            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null
              this.filteredUsers = []
              this.suggestionRange = null
              this.navigatedUserIndex = 0
              this.destroyPopup()
            },

            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              // pressing up arrow
              if (event.keyCode === 38) {
                this.upHandler()
                return true
              }

              // pressing down arrow
              if (event.keyCode === 40) {
                this.downHandler()
                return true
              }

              // pressing enter
              if (event.keyCode === 13) {
                this.enterHandler()
                return true
              }

              return false
            },

            onFilter: (items, query) => {
              if (!query) {
                return items
              }
              return fuzzySearch(items, query, ['name'], { threshold: 0 })
            }
          })
        ],
        onUpdate: _.debounce(({ getJSON, getHTML }) => {
          this.updateContent(getJSON(), getHTML(), this.images, this.attachments)
        }, 500)
      })
    },

    destroyEditor () {
      this.editor.destroy()
    },

    /**
     * navigate to the previous item. If it's the first item, navigate to the last one
     */
    upHandler () {
      this.navigatedUserIndex = ((this.navigatedUserIndex + this.filteredUsers.length) - 1) % this.filteredUsers.length
    },

    /**
     * navigate to the next item. If it's the last item, navigate to the first one
     */
    downHandler () {
      this.navigatedUserIndex = (this.navigatedUserIndex + 1) % this.filteredUsers.length
    },

    /**
     * Event handle for enter keypress
     */
    enterHandler () {
      const user = this.filteredUsers[this.navigatedUserIndex]
      if (user) {
        this.selectUser(user)
      }
    },

    /**
     * we have to replace our suggestion text with a mention so it's important to pass also the position of your suggestion text
     * @param {Object} user
     */
    selectUser (user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.email,
          label: user.name
        }
      })

      this.editor.focus()
    },

    /**
     * renders a popup with suggestions tiptap provides a virtualNode object for tippy.js for popups
     * @param {Object} node virtual dom node
     */
    renderPopup (node) {
      if (this.popup) {
        return
      }

      this.popup = tippy(node, {
        content: this.$refs.suggestions,
        trigger: 'mouseenter',
        interactive: true,
        theme: 'light',
        placement: 'top-start',
        inertia: true,
        duration: [400, 200],
        showOnInit: true,
        arrow: true,
        arrowType: 'round'
      })

      // we have to update tippy whenever the DOM is updated
      if (MutationObserver) {
        this.observer = new MutationObserver(() => {
          this.popup.popperInstance.scheduleUpdate()
        })

        this.observer.observe(this.$refs.suggestions, {
          childList: true,
          subtree: true,
          characterData: true
        })
      }
    },

    /**
     * Removes popup
     */
    destroyPopup () {
      if (this.popup) {
        this.popup.destroy()
        this.popup = null
      }
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    /**
     * Displays the Drop Zone container.
     */
    showDropZone () {
      this.$set(this.dropZone, 'visible', true)
    },

    /**
     * Updates the content of the content editor and sends it to the parent component.
     * @param {Object} json
     * @param {String} html
     * @param {Array} images
     * @param {Array} attachments
     */
    updateContent (json = this.value.JSON, html = this.value.HTML, images = this.value.IMAGES, attachments = this.value.ATTACHMENTS) {
      this.$emit('input', {
        JSON: json,
        HTML: html,
        IMAGES: images,
        ATTACHMENTS: attachments
      })
    },

    /**
     * Attaches the files placed into the Drop Zone and updates the content editor.
     * @param {Array} files
     */
    onAttachFiles (files) {
      if (!files.length) {
        return
      }

      this.attachments = this.value.ATTACHMENTS || []
      this.attachments.push(...files)
      this.updateContent(this.value.JSON, this.value.HTML, this.value.IMAGES, this.attachments)
    },

    /**
     * Embeds the given images into the Content Editor.
     * @param {Array} images
     */
    async onEmbedImages (images) {
      if (!images.length) {
        return
      }

      images.forEach(image => {
        this.editor.commands.image({ src: image.src })
      })

      this.images = this.value.IMAGES || []
      this.images.push(...images)
      this.updateContent(this.value.JSON, this.value.HTML, this.images, this.value.ATTACHMENTS)
    },

    /**
     * Removes an item from the preview container (attachments to upload).
     * @param {Number} index
     */
    deleteFile (index) {
      this.attachments.splice(index, 1)
    },

    /**
     * Trigger the menu for link edition to show up
     * @param Object attributes passed in, here is the link mark return by getMarkAttrs
     */
    showLinkMenu (attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },

    /**
     * Hide the menu and clear the link data
     */
    hideLinkMenu () {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },

    /**
     * Set the link for the mark using command
     */
    setLinkUrl (command, url) {
      command({ href: url })
      this.hideLinkMenu()
    }
  }
}
</script>

<style lang="scss" scoped>
.menubar {
  background-color: var(--color-light-gray);
}

.editor__menubar {
  border: 1px solid var(--color-gray-border);
  border-bottom: none;
  width: 100%;
}

.menubar__button {
  border: none;
  cursor: pointer;
  padding: 6px;
}

.menubar__button:hover, .is-active {
  background-color: gray;
  color: white;
}

.editor__content {
  ::v-deep .ProseMirror {
    min-height: var(--min-height);
    max-height: var(--max-height);
    width: 100%;
    border: var(--border);
    padding: 10px;
    overflow-y: scroll;
    background-color: var(--background-color);
  }

  ::v-deep .ProseMirror:focus {
    outline: none;
  }
}

.preview-container {
  padding-top: 8px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
}

.preview-container .item {
  margin-left: 8px;
  width: 56px;
  height: 56px;
  position: relative;
  display: flex;
  border: 1px solid var(--color-gray-border);
  border-radius: 8px;
}

.preview-container .item:first-child {
  margin-left: 0;
}

.preview-container .item img,
.preview-container .item div {
  width: 100%;
  height: 100%;
}

.preview-container .item .actions {
  display: none;
  position: absolute;
  background-color: rgba(0, 0, 0, .6);
  border-radius: 8px;
}

.preview-container .item:hover .actions {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
}

.mention {
  background: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}
.mention-suggestion {
  color: rgba(0, 0, 0, 0.6);
}
.suggestion-list {
  padding: 0.2rem;
  font-size: 0.8rem;
  font-weight: bold;
  max-height: 300px;
  overflow: scroll;
}
.suggestion-list__no-results {
  padding: 0.2rem 0.5rem;
}
.suggestion-list-item {
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  margin-bottom: 0.2rem;
  cursor: pointer;
}
.suggestion-list-item:last-child {
  margin-bottom: 0;
}
.suggestion-list-item.is-selected, .suggestion-list-item:hover {
  background-color: rgba(0, 0, 0, 0.2);
}
.suggestion-list-item.is-empty {
  opacity: 0.5;
}
</style>
